import React from 'react';

const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

const HIGHLIGHT_STYLE: React.CSSProperties = {
	backgroundColor: 'color-mix(in srgb, var(--colour-brand-primary, #2563eb) 18%, transparent)',
	color: 'inherit',
	fontWeight: 600,
	borderRadius: 2,
	padding: '0 1px',
};

/**
 * Splits `text` on whitespace-separated tokens of `query` and wraps each
 * case-insensitive match in a highlighted `<span>`. Use to highlight search-term
 * matches inside combobox dropdown items, autocomplete suggestions, etc.
 *
 * Uses inline styles instead of a class so it works inside react-select's
 * portaled menu regardless of how the host page's CSS bundle is scoped.
 */
export function highlightMatches(text: string, query: string | null | undefined): React.ReactNode {
	if (!text) {
		return text;
	}
	const tokens = (query ?? '').trim().split(/\s+/).filter(Boolean);
	if (tokens.length === 0) {
		return text;
	}

	const escaped = tokens.map(t => t.replace(REGEX_SPECIAL_CHARS, '\\$&'));
	const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
	const parts = text.split(regex);
	const lowerTokens = new Set(tokens.map(t => t.toLowerCase()));

	// Every part gets its own <span>, never a bare text node. Google Translate replaces text
	// nodes with <font> elements, so a bare text node beside a sibling element is gone by the
	// next keystroke and React's removeChild/insertBefore throws NotFoundError — which the
	// error boundary turns into the Whoops page.
	let offset = 0;
	return parts
		.filter(part => part !== '')
		.map(part => {
			const key = `${offset}:${part}`;
			offset += part.length;
			return (
				<span key={key} style={lowerTokens.has(part.toLowerCase()) ? HIGHLIGHT_STYLE : undefined}>
					{part}
				</span>
			);
		});
}
